<script setup lang="ts">
	import {
		BuildingOfficeIcon,
		CalendarDaysIcon,
		EllipsisHorizontalIcon,
		UserIcon,
	} from '@heroicons/vue/24/solid'
	import { Icon } from '@iconify/vue'
	import { computed, defineAsyncComponent } from 'vue'

	import StyleTable from '@/components/StyleTable.vue'
	import StyledTableBody from '@/components/StyledTableBody.vue'
	import StyledTableCell from '@/components/StyledTableCell.vue'
	import StyledTableHead from '@/components/StyledTableHead.vue'
	import StyledTableHeader from '@/components/StyledTableHeader.vue'
	import StyledTableRow from '@/components/StyledTableRow.vue'

	import { PaginatedResponse } from '@/interfaces/paginatedResponse'

	import { Cef, UsedService } from '../interfaces/cef'
	import NoCefsMessage from './NoCefsMessage.vue'

	const ServiceIcon = defineAsyncComponent(
		() => import('@/components/ServiceIcon.vue')
	)

	interface Props {
		cefs: PaginatedResponse<Cef>
		selectedCef?: Cef | null
		isFetching?: boolean
		isLoading?: boolean
		isRefetching?: boolean
	}

	interface Emits {
		(e: 'select-cef', cef: Cef): void
		(e: 'close-preview-request'): void
		(e: 'refetch'): void
	}

	defineProps<Props>()
	const emits = defineEmits<Emits>()

	const statusMap: Record<
		string,
		{ background: string; text: string; message: string; icon: string }
	> = {
		confirmado: {
			background: 'bg-emerald-50 border-emerald-200',
			text: 'text-emerald-700',
			message: 'Confirmado',
			icon: 'ic:round-check-circle',
		},
		creado: {
			background: 'bg-amber-50 border-amber-200',
			text: 'text-amber-700',
			message: 'Pendiente',
			icon: 'ic:round-schedule',
		},
		cancelado: {
			background: 'bg-red-50 border-red-200',
			text: 'text-red-700',
			message: 'Cancelado',
			icon: 'ic:round-cancel',
		},
		facturado: {
			background: 'bg-emerald-50 border-emerald-200',
			text: 'text-emerald-700',
			message: 'Facturado',
			icon: 'ic:round-check-circle',
		},
		pagado: {
			background: 'bg-emerald-50 border-emerald-200',
			text: 'text-emerald-700',
			message: 'Pagado',
			icon: 'ic:round-check-circle',
		},
		procesada: {
			background: 'bg-emerald-50 border-emerald-200',
			text: 'text-emerald-700',
			message: 'Confirmado',
			icon: 'ic:round-check-circle',
		},
	}

	const getStatus = (state: string) =>
		computed(() => {
			const normalizedState = (state || '').toLowerCase().trim()
			return statusMap[normalizedState] || {
				background: 'bg-slate-50 border-slate-200',
				text: 'text-slate-700',
				message: state || 'En proceso',
				icon: 'ic:round-schedule',
			}
		})

	const providerNames = (service: UsedService) =>
		Array.from(new Set(service.providers.map((provider) => provider.suppliableName).filter(Boolean)))

	const allProviderNames = (cef: Cef) =>
		Array.from(
			new Set(
				cef.usedServices
					.filter((service) => service.usedTimes > 0)
					.flatMap((service) => providerNames(service))
			)
		)

	const splitActivityPeriod = (activityPeriod: string) => {
		const [checkIn, checkOut] = activityPeriod.split(' - ')
		return checkIn && checkOut ? { checkIn, checkOut } : null
	}

</script>

<template>
	<div class="flex gap-2">
		<!-- Vista Móvil Empresarial -->
		<section class="lg:hidden w-full relative">
			<!-- Loading overlay para móvil -->
			<transition
				enter-active-class="transition-all duration-300 ease-out"
				enter-from-class="opacity-0"
				enter-to-class="opacity-100"
				leave-active-class="transition-all duration-200 ease-in"
				leave-from-class="opacity-100"
				leave-to-class="opacity-0"
			>
				<div
					v-if="isFetching && !isLoading"
					class="absolute inset-0 bg-white/90 backdrop-blur-sm z-20 flex items-center justify-center rounded-xl"
				>
					<div
						class="flex flex-col items-center gap-4 p-6 bg-white rounded-xl shadow-lg border border-gray-100"
					>
						<div class="relative">
							<div
								class="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"
							></div>
							<div
								class="absolute inset-0 w-10 h-10 border-4 border-transparent border-t-blue-400 rounded-full animate-spin"
								style="animation-duration: 0.75s"
							></div>
						</div>
						<div class="text-center">
							<p class="text-sm font-semibold text-gray-900 mb-1">
								Cargando itinerarios...
							</p>
							<p class="text-xs text-gray-500">Por favor espera un momento</p>
						</div>
					</div>
				</div>
			</transition>

			<div
				class="space-y-4"
				:class="{
					'opacity-50 pointer-events-none': isFetching,
				}"
			>
				<!-- Contenido móvil cuando hay datos -->
				<template v-if="cefs.data.length > 0">
					<div
						v-for="cef in cefs.data"
						:key="cef.id"
						class="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer group"
						@click="emits('select-cef', cef)"
					>
						<!-- Header de la tarjeta -->
						<div class="p-5 border-b border-gray-100">
							<div class="flex justify-between items-start mb-3">
								<div class="flex items-center gap-3">
									<div
										class="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"
									>
										<BuildingOfficeIcon class="w-5 h-5 text-blue-600" />
									</div>
									<div>
										<p class="text-xs text-gray-500 font-medium">
											{{ cef.companyName }}
										</p>
									</div>
								</div>
								<div
									:class="[
										getStatus(cef.state).value.background,
										getStatus(cef.state).value.text,
									]"
									class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold"
								>
									<Icon
										:icon="getStatus(cef.state).value.icon"
										class="w-3 h-3"
									/>
									{{ getStatus(cef.state).value.message }}
								</div>
							</div>

							<h3 class="font-semibold text-gray-900 mb-2 leading-tight">
								{{ cef.name }}
							</h3>

							<div class="flex items-center gap-2 text-sm text-gray-600">
								<UserIcon class="w-4 h-4 text-gray-400" />
								<span>{{ cef.travelerName }}</span>
							</div>
						</div>

						<!-- Servicios utilizados -->
						<div class="p-5 border-b border-gray-100 bg-gray-50">
							<h4
								class="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3"
							>
								Servicios
							</h4>
							<div class="space-y-3">
								<template
									v-for="service in cef.usedServices"
									:key="service.name"
								>
									<div v-if="service.usedTimes > 0">
										<div class="flex items-center gap-3 mb-2">
											<div
												class="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm"
											>
												<ServiceIcon
													:service="service.name"
													size="text-lg"
												/>
											</div>
											<div class="flex-1">
												<span class="text-sm font-medium text-gray-900">{{
													service.displayName
												}}</span>
												<span
													class="ml-2 text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full"
												>
													{{ service.usedTimes }}x
												</span>
											</div>
										</div>

										<div class="ml-11 space-y-1">
											<div
												v-for="provider in service.providers"
												:key="provider.suppliableName"
												class="text-xs text-gray-600 bg-white rounded-lg p-2 border border-gray-100"
											>
												<div class="font-medium text-gray-800 mb-1">
													{{ provider.suppliableName }}
												</div>
												<div class="flex justify-between items-center">
													<span class="text-gray-500"
														>Confirmación:
														{{ provider.confirmation }}</span
													>
													<span class="text-gray-500">
														{{
															new Date(provider.startDate).toLocaleDateString()
														}}
														-
														{{
															new Date(provider.endDate).toLocaleDateString()
														}}
													</span>
												</div>
											</div>
										</div>
									</div>
								</template>
							</div>
						</div>

						<!-- Footer -->
						<div class="p-5 flex justify-between items-center">
							<div class="flex items-center gap-2 text-sm text-gray-600">
								<CalendarDaysIcon class="w-4 h-4 text-gray-400" />
								<span>{{ cef.activityPeriod }}</span>
							</div>
							<button
								class="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 group-hover:translate-x-1 transition-all duration-200"
							>
								Ver detalles
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M9 5l7 7-7 7"
									></path>
								</svg>
							</button>
						</div>
					</div>
				</template>

				<!-- Mensaje cuando no hay datos en móvil -->
				<template v-else>
					<div class="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
						<NoCefsMessage :filter-search="false" />
					</div>
				</template>
			</div>
		</section>
	</div>

	<!-- Vista Desktop Empresarial -->
	<div class="hidden lg:flex gap-2">
		<section class="overflow-x-auto w-full relative">
			<!-- Loading overlay para desktop -->
			<transition
				enter-active-class="transition-all duration-300 ease-out"
				enter-from-class="opacity-0"
				enter-to-class="opacity-100"
				leave-active-class="transition-all duration-200 ease-in"
				leave-from-class="opacity-100"
				leave-to-class="opacity-0"
			>
				<div
					v-if="isFetching && !isLoading"
					class="absolute inset-0 bg-white/90 backdrop-blur-sm z-20 flex items-center justify-center rounded-xl"
				>
					<div
						class="flex flex-col items-center gap-4 p-6 bg-white rounded-xl shadow-lg border border-gray-100"
					>
						<div class="relative">
							<div
								class="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"
							></div>
							<div
								class="absolute inset-0 w-10 h-10 border-4 border-transparent border-t-blue-400 rounded-full animate-spin"
								style="animation-duration: 0.75s"
							></div>
						</div>
						<div class="text-center">
							<p class="text-sm font-semibold text-gray-900 mb-1">
								Cargando itinerarios...
							</p>
							<p class="text-xs text-gray-500">Por favor espera un momento</p>
						</div>
					</div>
				</div>
			</transition>

			<div
				class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
				:class="{
					'opacity-50 pointer-events-none': isFetching,
				}"
			>
				<StyleTable>
					<StyledTableHeader class="bg-gray-50 border-b border-gray-200">
						<StyledTableHead
							class="w-24 lg:w-[135px] text-center font-semibold text-gray-700"
						>
							Servicios
						</StyledTableHead>
						<StyledTableHead
							class="w-48 lg:w-[287px] text-left font-semibold text-gray-700"
						>
							Itinerario
						</StyledTableHead>
						<StyledTableHead
							class="w-40 lg:w-[257px] text-left font-semibold text-gray-700"
						>
							Fechas
						</StyledTableHead>
						<StyledTableHead
							class="w-36 lg:w-[225px] text-left font-semibold text-gray-700"
						>
							Viajero
						</StyledTableHead>
						<StyledTableHead
							class="w-32 lg:w-[157.5px] text-center font-semibold text-gray-700"
						>
							Estatus
						</StyledTableHead>
					</StyledTableHeader>

					<StyledTableBody>
						<template v-if="cefs.data.length > 0">
							<StyledTableRow
								v-for="(cef, index) in cefs.data"
								:key="index"
								class="hover:bg-blue-50 transition-all duration-200 cursor-pointer border-b border-gray-100 group"
								:class="cef.id === selectedCef?.id ? 'bg-blue-50' : ''"
								@click="emits('select-cef', cef)"
							>
								<!-- Servicios -->
								<StyledTableCell class="w-24 lg:w-[135px]">
									<div class="flex items-center justify-center gap-1 flex-wrap">
										<template
											v-for="service in cef.usedServices"
											:key="service.name"
										>
											<div
												v-if="service.usedTimes"
												class="relative group/service"
												v-tooltip="
													providerNames(service).length > 0 ?
														`${service.displayName}: ${providerNames(service).join(', ')}`
													:	service.displayName
												"
											>
												<div
													class="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center border border-gray-200 hover:bg-blue-50 hover:border-blue-200 transition-colors"
												>
													<ServiceIcon
														:service="service.name"
														size="text-lg"
													/>
												</div>
												<span
													v-if="service.usedTimes > 1"
													class="absolute -top-2 -right-2 w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center"
												>
													{{ service.usedTimes }}
												</span>
												<Icon
													icon="ic:round-check-circle"
													class="absolute -bottom-1 -right-1 w-4 h-4 text-green-500 bg-white rounded-full"
												/>
											</div>
										</template>
									</div>
								</StyledTableCell>

								<!-- Itinerario -->
								<StyledTableCell class="w-48 lg:w-[287px]">
									<div class="flex flex-col gap-2">
										<div class="flex items-start justify-between">
											<span
												class="font-semibold text-gray-900 leading-tight text-sm line-clamp-2"
											>
												{{ cef.name }}
											</span>
										</div>

										<div
											v-if="allProviderNames(cef).length > 0"
											class="flex flex-col gap-0.5"
										>
											<template
												v-for="service in cef.usedServices"
												:key="service.name"
											>
												<div
													v-if="service.usedTimes > 0 && providerNames(service).length > 0"
													class="flex items-center gap-1"
												>
													<ServiceIcon
														:service="service.name"
														size="text-xs"
													/>
													<span
														class="text-xs text-gray-500 truncate"
														:title="providerNames(service).join(', ')"
													>
														{{ providerNames(service).join(', ') }}
													</span>
												</div>
											</template>
										</div>

										<div class="flex items-center gap-2">
											<div
												class="w-4 h-4 bg-blue-100 rounded flex items-center justify-center flex-shrink-0"
											>
												<BuildingOfficeIcon class="w-3 h-3 text-blue-600" />
											</div>
											<span class="text-xs text-gray-600 font-medium truncate">
												{{ cef.companyName }}
											</span>
										</div>

										<div class="flex items-center gap-2">
											<div
												class="w-4 h-4 bg-gray-100 rounded flex items-center justify-center flex-shrink-0"
											>
												<span class="text-xs font-mono font-bold text-gray-500"
													>#</span
												>
											</div>
											<span class="text-xs text-gray-500 font-mono">
												{{ cef.id }}
											</span>
										</div>
									</div>
								</StyledTableCell>

								<!-- Fechas -->
								<StyledTableCell class="w-40 lg:w-[257px]">
									<div class="flex items-start gap-2">
										<CalendarDaysIcon class="w-4 h-4 text-gray-400 mt-0.5" />
										<div
											v-if="splitActivityPeriod(cef.activityPeriod)"
											class="flex flex-col gap-1.5 text-sm text-gray-700 font-medium"
										>
											<div>
												<div>{{ splitActivityPeriod(cef.activityPeriod)!.checkIn }}</div>
												<div class="text-xs font-normal text-gray-400">check in</div>
											</div>
											<div>
												<div>{{ splitActivityPeriod(cef.activityPeriod)!.checkOut }}</div>
												<div class="text-xs font-normal text-gray-400">check out</div>
											</div>
										</div>
										<span
											v-else
											class="text-sm text-gray-700 font-medium"
											>{{ cef.activityPeriod }}</span
										>
									</div>
								</StyledTableCell>

								<!-- Viajero -->
								<StyledTableCell class="w-36 lg:w-[225px]">
									<div class="flex items-center gap-2">
										<UserIcon class="w-4 h-4 text-gray-400" />
										<span class="text-sm text-gray-700 font-medium truncate">{{
											cef.travelerName
										}}</span>
									</div>
								</StyledTableCell>

								<!-- Estado -->
								<StyledTableCell class="w-32 lg:w-[157.5px]">
									<div class="flex justify-center">
										<div
											:class="[
												getStatus(cef.state).value.background,
												getStatus(cef.state).value.text,
											]"
											class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold"
										>
											<Icon
												:icon="getStatus(cef.state).value.icon"
												class="w-3 h-3"
											/>
											{{ getStatus(cef.state).value.message }}
										</div>
									</div>
								</StyledTableCell>
							</StyledTableRow>
						</template>

						<template v-else>
							<StyledTableRow>
								<StyledTableCell
									colspan="7"
									class="py-16"
								>
									<NoCefsMessage :filter-search="false" />
								</StyledTableCell>
							</StyledTableRow>
						</template>
					</StyledTableBody>
				</StyleTable>
			</div>
		</section>
	</div>
</template>

<style scoped>
	/* Animaciones suaves */
	.transition-all {
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
	}

	/* Spinner personalizado */
	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}

	.animate-spin {
		animation: spin 1s linear infinite;
	}

	/* Backdrop blur para navegadores que lo soporten */
	@supports (backdrop-filter: blur(8px)) {
		.backdrop-blur-sm {
			backdrop-filter: blur(8px);
		}
	}

	/* Efectos hover mejorados */
	.group:hover .group-hover\:translate-x-1 {
		transform: translateX(0.25rem);
	}

	.group:hover .group-hover\:scale-110 {
		transform: scale(1.1);
	}
</style>
